import { Component, Input } from '@angular/core';

export type StatusChipVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'primary'
  | 'muted'
  | 'default';

export interface StatusChipConfig {
  label?: string;
  variant?: StatusChipVariant | string;
  icon?: string;
  customBg?: string;
  customColor?: string;
  compact?: boolean;
}

export type StatusChipConfigMap = Record<string, StatusChipConfig>;

const DEFAULT_STATUS_VARIANTS: Record<
  string,
  { variant: StatusChipVariant; label?: string }
> = {
  TAX: { variant: 'success', label: 'TAX' },
  TAX_INVOICE: { variant: 'success', label: 'TAX' },
  SUCCESS: { variant: 'success' },
  PAID: { variant: 'success' },
  ACTIVE: { variant: 'success' },
  COMPLETED: { variant: 'success' },
  SETTLED: { variant: 'success' },
  CONFIRMED: { variant: 'success' },

  PROFORMA: { variant: 'warning', label: 'PROFORMA' },
  PROFORMA_INVOICE: { variant: 'warning', label: 'PROFORMA' },
  PENDING: { variant: 'warning' },
  INITIATED: { variant: 'warning' },
  DRAFT: { variant: 'warning' },
  DUE: { variant: 'warning' },

  VOID: { variant: 'danger', label: 'VOID' },
  CANCEL: { variant: 'danger', label: 'CANCELLED' },
  CANCELLED: { variant: 'danger', label: 'CANCELLED' },
  FAILED: { variant: 'danger' },
  EXPIRED: { variant: 'danger' },
  UNPAID: { variant: 'danger' },

  CN: { variant: 'info', label: 'CN' },
  CREDIT_NOTE: { variant: 'info', label: 'CREDIT NOTE' },
  INFO: { variant: 'info' },

  DN: { variant: 'primary', label: 'DN' },
  DEBIT_NOTE: { variant: 'primary', label: 'DEBIT NOTE' },
  PRIMARY: { variant: 'primary' },
};

@Component({
  selector: 'hospitality-bot-status-chip',
  templateUrl: './status-chip.component.html',
  styleUrls: ['./status-chip.component.scss'],
})
export class StatusChipComponent {
  @Input() status?: string;
  @Input() state?: string;
  @Input() label?: string;
  @Input() text?: string;
  @Input() variant?: StatusChipVariant | string;
  @Input() config?: StatusChipConfigMap | StatusChipConfig;
  @Input() icon?: string;
  @Input() compact = false;
  @Input() customBg?: string;
  @Input() customColor?: string;

  get rawKey(): string {
    return (this.status || this.state || '').trim();
  }

  get resolvedConfig(): StatusChipConfig {
    const raw = this.rawKey;
    const upperKey = raw.toUpperCase();

    if (this.config) {
      const map = this.config as StatusChipConfigMap;
      if (raw && map[raw]) {
        return map[raw];
      }
      if (upperKey && map[upperKey]) {
        return map[upperKey];
      }
      const single = this.config as StatusChipConfig;
      if (single.variant || single.label) {
        return single;
      }
    }

    if (upperKey && DEFAULT_STATUS_VARIANTS[upperKey]) {
      return DEFAULT_STATUS_VARIANTS[upperKey];
    }

    return {};
  }

  get displayLabel(): string {
    if (this.label) return this.label;
    if (this.text) return this.text;
    if (this.resolvedConfig.label) return this.resolvedConfig.label;

    const s = this.rawKey;
    if (!s) return '';
    return s.replace(/_/g, ' ');
  }

  get chipClass(): string {
    const v = this.variant || this.resolvedConfig.variant || 'muted';
    return `chip--${v}`;
  }

  get displayIcon(): string | undefined {
    return this.icon || this.resolvedConfig.icon;
  }

  get bg(): string | undefined {
    return this.customBg || this.resolvedConfig.customBg;
  }

  get color(): string | undefined {
    return this.customColor || this.resolvedConfig.customColor;
  }

  get isCompact(): boolean {
    return this.compact || !!this.resolvedConfig.compact;
  }
}
